/**
 * Accessibility, as decisions rather than as an audit.
 *
 * A mail client is a list, a reading pane and a set of actions, which is the shape screen readers
 * handle best and the shape most applications still get wrong — usually by announcing everything,
 * which is the same as announcing nothing.
 */

/**
 * What is announced to a screen reader, and how urgently.
 *
 * `assertive` interrupts whatever is being read. It is reserved for things that change what
 * somebody is about to do; using it for a save confirmation makes the product talk over itself.
 */
export type Politeness = 'off' | 'polite' | 'assertive';

export const ANNOUNCEMENTS: Record<string, Politeness> = {
  /* The result of something they did. Polite: they know they did it. */
  action_completed: 'polite',
  action_undone: 'polite',
  batch_progress: 'off',
  /* Off, not polite. A count that updates every second is a screen reader that never stops. */
  live_counter: 'off',

  /* Interrupts, because it changes what they should do next. */
  action_failed: 'assertive',
  session_expired: 'assertive',
  /* The one notification that outranks reading: the product refused to do something destructive. */
  destructive_refused: 'assertive',

  navigation: 'polite',
  new_mail: 'polite',
  filter_changed: 'polite',
};

/**
 * How long an undo toast stays focusable.
 *
 * Longer than it is visible. A toast that disappears after eight seconds is a toast somebody using
 * a screen reader has not reached yet, and an undo they cannot reach is not an undo.
 */
export const TOAST_FOCUSABLE_MS = 30_000;

/**
 * Contrast, as numbers rather than as a promise.
 *
 * WCAG AA: 4.5 for body text, 3.0 for large text and for the boundary of a control. The third is
 * the one that gets missed — a button whose label passes and whose border does not is a button
 * somebody cannot find.
 */
export const MIN_CONTRAST_BODY = 4.5;
export const MIN_CONTRAST_LARGE = 3.0;
export const MIN_CONTRAST_BOUNDARY = 3.0;

/**
 * Whether a colour pair is good enough for its role.
 *
 * Takes a computed ratio rather than colours, because computing a ratio correctly is a solved
 * problem and re-solving it here would be a second implementation to keep right.
 */
export function passesContrast(ratio: number, role: 'body' | 'large' | 'boundary'): boolean {
  const minimum =
    role === 'body'
      ? MIN_CONTRAST_BODY
      : role === 'large'
        ? MIN_CONTRAST_LARGE
        : MIN_CONTRAST_BOUNDARY;
  return ratio >= minimum;
}

/**
 * Colour is never the only signal.
 *
 * The priority bands, the risk labels and the health states all carry a word as well as a colour.
 * Listed here so that adding a fourth band without a label is a test failure rather than a
 * discovery.
 */
export const NEEDS_NON_COLOUR_SIGNAL: readonly string[] = [
  'priority_band',
  'decision_risk',
  'mailbox_health',
  'subscription_status',
  'anomaly_severity',
];

/**
 * Where focus goes after an action.
 *
 * The question every keyboard-driven interface gets wrong at least once: somebody archives the item
 * they were on, and focus lands on the document body, and the next `j` does nothing.
 */
export type FocusTarget = 'next_item' | 'previous_item' | 'list' | 'toast' | 'restore' | 'dialog';

export function focusAfter(input: {
  action: string;
  removedFromList: boolean;
  hasNextItem: boolean;
  undoable: boolean;
}): FocusTarget {
  /*
   * The undo toast wins when there is one.
   *
   * It is the only element with a time limit on it, and somebody who cannot see the screen has no
   * other way to know it exists.
   */
  if (input.undoable) return 'toast';
  if (!input.removedFromList) return 'restore';
  return input.hasNextItem ? 'next_item' : 'list';
}

/**
 * Motion.
 *
 * `prefers-reduced-motion` is honoured by removing the animation, not by shortening it. A fast
 * version of a movement that makes somebody nauseous is a fast way to make them nauseous.
 *
 * Opacity transitions stay: they are not motion, and removing them makes state changes appear
 * without any transition at all, which is its own kind of jarring.
 */
export const REDUCED_MOTION_KEEPS: readonly string[] = ['opacity', 'color', 'background-color'];
